import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import SEO from "@/components/marketing/SEO";

const Geneve = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Piktable Genève",
    "description": "Système de réservation pour restaurants à Genève",
    "url": "https://piktable.ch/geneve",
    "areaServed": {
      "@type": "City",
      "name": "Genève",
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": "Canton de Genève"
      }
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "46.2044",
      "longitude": "6.1432"
    },
    "priceRange": "CHF 29-49",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Genève",
      "addressCountry": "CH"
    },
    "email": "contact@piktable.ch",
    "sameAs": ["https://www.instagram.com/piktable"],
    "image": "https://piktable.ch/screenshots/dashboard-overview.png"
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://piktable.ch/" },
      { "@type": "ListItem", "position": 2, "name": "Genève", "item": "https://piktable.ch/geneve" }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Système de réservation pour restaurants à Genève"
        description="Piktable accompagne les restaurants genevois dans la gestion de leurs réservations. Solution suisse romande adaptée aux établissements de Genève."
        canonical="/geneve"
        structuredData={[structuredData, breadcrumbData]}
      />
      <Header />
      <main className="pt-16">
        <section className="py-24 bg-gradient-hero">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h1 className="font-playfair text-5xl md:text-6xl font-bold text-foreground">
                Système de réservation pour restaurants à Genève
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Piktable accompagne les restaurants genevois dans la gestion de leurs réservations. Une solution suisse romande, simple et adaptée aux établissements de la région lémanique.
              </p>
              <div className="flex justify-center pt-6">
                <Button size="lg" onClick={() => navigate("/signup")}>
                  Essayer gratuitement
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto space-y-12">
              <div className="text-center">
                <h2 className="font-playfair text-4xl md:text-5xl font-bold text-foreground mb-6">
                  Piktable pour les restaurants genevois
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">🌍</div>
                  <h3 className="text-2xl font-bold text-foreground">Conçu pour Genève</h3>
                  <p className="text-muted-foreground">
                    Solution développée en Suisse romande qui comprend les spécificités des restaurants genevois et leur clientèle internationale.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">📱</div>
                  <h3 className="text-2xl font-bold text-foreground">Widget moderne</h3>
                  <p className="text-muted-foreground">
                    Interface de réservation élégante et intuitive, parfaitement optimisée pour mobile. Vos clients réservent en 2 clics.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">💼</div>
                  <h3 className="text-2xl font-bold text-foreground">Gestion professionnelle</h3>
                  <p className="text-muted-foreground">
                    Vue en temps réel de vos tables, combinaison de tables, historique des réservations et accès multi-staff pour toute votre équipe.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">🔒</div>
                  <h3 className="text-2xl font-bold text-foreground">Données sécurisées</h3>
                  <p className="text-muted-foreground">
                    Hébergement conforme aux standards suisses et européens (RGPD). Vos données et celles de vos clients sont protégées.
                  </p>
                </div>
              </div>

              <div className="mt-12 space-y-4">
                <h3 className="font-playfair text-2xl font-bold text-foreground">
                  Les défis spécifiques des restaurants genevois
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Genève accueille une clientèle internationale et multilingue, avec une forte rotation liée au tourisme, aux organisations internationales et aux événements. Les restaurants genevois font face à des pics de fréquentation imprévisibles et à une clientèle habituée aux standards numériques. Que vous soyez aux Eaux-Vives, à Carouge, aux Pâquis ou à Plainpalais, Piktable vous aide à gérer ces flux avec un widget de réservation optimisé pour mobile et des confirmations automatiques par e-mail. Une solution suisse romande qui comprend le rythme genevois.
                </p>
              </div>

              <div className="mt-16 p-8 rounded-2xl bg-primary/5 border-2 border-primary/20">
                <h3 className="font-playfair text-3xl font-bold text-foreground mb-4 text-center">
                  Idéal pour votre établissement genevois
                </h3>
                <p className="text-lg text-muted-foreground text-center mb-8">
                  Des Eaux-Vives à Carouge, du centre-ville aux Pâquis, Piktable s'adapte à tous les types de restaurants genevois.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="w-full sm:w-auto" onClick={() => navigate("/signup")}>
                    Essayer gratuitement
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="w-full sm:w-auto"
                  >
                    En savoir plus
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Geneve;
